import { useState, useEffect } from 'react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts'
import { dashboardApi, alertesApi } from '../api'
import { useApp } from '../AppContext'
import { StatCard, BadgeStatut, BadgeType, fmt, fmtDate, Loading } from '../components/ui'

const MOIS   = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
const COLORS = ['#2e7d52','#1565c0','#e65100','#6a1b9a','#00838f','#c62828','#37474f']

const TYPES_FR = {
  devis:'Devis', facture:'Facture', bon_commande:'Bon commande',
  bon_livraison:'Bon livraison', avoir:'Avoir'
}

export default function Dashboard() {
  const { toast, isAdmin } = useApp()
  const [data, setData]     = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    dashboardApi.stats()
      .then(r => setData(r.data))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="content"><Loading /></div>
  if (!data)   return null

  return (
    <div className="content">
      {data.role === 'admin'
        ? <DashboardAdmin data={data} />
        : <DashboardTech  data={data} />
      }
    </div>
  )
}

// ── Dashboard Admin ───────────────────────────────────────────
function DashboardAdmin({ data }) {
  const evol = data.ca_mois_prec > 0
    ? ((data.ca_mois - data.ca_mois_prec) / data.ca_mois_prec * 100).toFixed(1)
    : null

  const chartData = (data.ca_mensuel || []).map(m => ({
    name:    MOIS[m.mois - 1],
    'CA TTC': parseFloat(m.ca_ttc).toFixed(2),
  }))

  const pieData = (data.par_type || []).map(p => ({
    name:  TYPES_FR[p.type] || p.type,
    value: parseInt(p.nb),
  }))

  return (
    <>
      {/* KPIs ligne 1 */}
      <div className="grid4" style={{ marginBottom: 14 }}>
        <StatCard label="CA ce mois"      value={fmt(data.ca_mois)}    icon="report-money"
          trend={evol ? { up: evol > 0, label: `${evol > 0 ? '+' : ''}${evol}% vs mois préc.` } : null} />
        <StatCard label="Impayés"          value={fmt(data.impayes?.total)} icon="clock-dollar" color="danger" />
        <StatCard label="En retard"        value={`${data.retard?.nb || 0} facture(s)`} icon="alert-triangle" color="danger" />
        <StatCard label="Devis en attente" value={data.devis_attente || 0} icon="file-description" />
      </div>

      {/* KPIs ligne 2 */}
      <div className="grid4" style={{ marginBottom: 18 }}>
        <StatCard label="Techniciens actifs"   value={data.techniciens || 0}        icon="users-group" />
        <StatCard label="Rapports cette semaine" value={data.rapports_nouveaux || 0} icon="report" />
        <StatCard label="Alertes actives"       value={data.alertes || 0}           icon="bell" color={data.alertes > 0 ? 'danger' : 'primary'} />
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Taux transfo devis → facture</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>{data.taux_transfo || 0}%</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
            {data.devis_transformes || 0} / {data.total_devis || 0} devis
          </div>
          {/* Barre de progression */}
          <div style={{ marginTop: 8, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${data.taux_transfo || 0}%`, height: '100%', background: 'var(--primary)', borderRadius: 3, transition: 'width .5s' }} />
          </div>
        </div>
      </div>

      <div className="grid2" style={{ marginBottom: 14 }}>
        {/* Graphique CA */}
        <div className="card">
          <div className="card-title">Évolution du CA (12 mois)</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#2e7d52" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2e7d52" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text3)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text3)' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} labelStyle={{ color: 'var(--text)' }} />
                <Area type="monotone" dataKey="CA TTC" stroke="#2e7d52" strokeWidth={2} fill="url(#colorCA)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)' }}>
              Pas encore de données
            </div>
          )}
        </div>

        {/* Répartition par type */}
        <div className="card">
          <div className="card-title">Répartition documents (année)</div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text3)' }}>
              Pas encore de données
            </div>
          )}
        </div>
      </div>

      <div className="grid2" style={{ marginBottom: 14 }}>
        {/* Top clients */}
        <div className="card">
          <div className="card-title">Top clients (année)</div>
          {data.top_clients?.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.top_clients.map(c => ({ name: c.nom.split(' ').slice(0,2).join(' '), total: parseFloat(c.total) }))} layout="vertical" margin={{ left: 0, right: 16 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill:'var(--text3)' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill:'var(--text)' }} width={90} />
                <Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="total" fill="#2e7d52" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding:'32px 0', textAlign:'center', color:'var(--text3)', fontSize:13 }}>Aucun client cette année</div>
          )}
        </div>

        {/* Rapports équipe */}
        <div className="card">
          <div className="card-title">
            Rapports équipe récents
            <a href="/rapports" style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>Voir tout →</a>
          </div>
          {data.rapports_equipe?.length ? (
            <div>
              {data.rapports_equipe.map((r, i) => (
                <div key={r.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 0', borderBottom: i < data.rapports_equipe.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width:30, height:30, borderRadius:'50%', background:'#f3e5f5', color:'#6a1b9a', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>
                    {(r.technicien_nom||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{r.technicien_nom}</div>
                    <div style={{ fontSize:12, color:'var(--text3)' }}>{fmtDate(r.date_rapport)}</div>
                    <div style={{ fontSize:12, color:'var(--text2)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.contenu}</div>
                  </div>
                  <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:10, background: r.statut==='soumis'?'#e8f5e9':'#fff8e1', color: r.statut==='soumis'?'#2e7d32':'#e65100', flexShrink:0 }}>
                    {r.statut==='soumis'?'Soumis':'Brouillon'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding:'24px 0', textAlign:'center', color:'var(--text3)', fontSize:13 }}>Aucun rapport cette semaine</div>
          )}
        </div>
      </div>

      {/* Derniers documents */}
      <div className="card" style={{ padding: 0 }}>
        <div className="card-title" style={{ padding:'14px 18px 0' }}>Derniers documents</div>
        {data.derniers_docs?.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Numéro</th><th>Type</th><th>Contact</th><th>Date</th><th>Montant TTC</th><th>Statut</th></tr>
              </thead>
              <tbody>
                {data.derniers_docs.map(d => (
                  <tr key={d.id}>
                    <td><strong>{d.numero}</strong></td>
                    <td><BadgeType type={d.type} /></td>
                    <td>{d.contact_nom}</td>
                    <td>{fmtDate(d.date_document)}</td>
                    <td><strong>{fmt(d.total_ttc)}</strong></td>
                    <td><BadgeStatut statut={d.statut} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding:32, textAlign:'center', color:'var(--text3)', fontSize:13 }}>Aucun document</div>
        )}
      </div>
    </>
  )
}

// ── Dashboard Technicien ──────────────────────────────────────
function DashboardTech({ data }) {
  const today = new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'2-digit', month:'long' })

  return (
    <>
      {/* Bonjour + état du rapport */}
      <div style={{ background: data.rapport_jour ? 'var(--primary-light)' : '#fff8e1', border: `1px solid ${data.rapport_jour ? 'var(--g100)' : '#ffe082'}`, borderRadius:'var(--radius-lg)', padding:'16px 20px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16, color: data.rapport_jour ? 'var(--primary)' : '#e65100' }}>
            {data.rapport_jour ? '✓ Rapport du jour soumis' : '⚠ Rapport du jour non rédigé'}
          </div>
          <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>{today}</div>
        </div>
        {!data.rapport_jour && (
          <a href="/rapports" className="btn primary" style={{ textDecoration:'none' }}>
            <i className="ti ti-report" /> Rédiger
          </a>
        )}
        {data.rapport_jour && data.rapport_jour.statut === 'brouillon' && (
          <a href="/rapports" className="btn" style={{ textDecoration:'none' }}>
            <i className="ti ti-send" /> Soumettre
          </a>
        )}
      </div>

      {/* KPIs technicien */}
      <div className="grid4" style={{ marginBottom:18 }}>
        <StatCard label="Alertes actives"    value={data.alertes || 0}          icon="bell"     color={data.alertes > 0 ? 'danger' : 'primary'} />
        <StatCard label="Alertes en retard"  value={data.alertes_retard || 0}   icon="alert-triangle" color={data.alertes_retard > 0 ? 'danger' : 'primary'} />
        <StatCard label="Rapports (7 jours)" value={data.rapports_semaine || 0} icon="report" />
        <StatCard label="Credentials accès"  value={data.credentials || 0}      icon="key" />
      </div>

      <div className="grid2" style={{ marginBottom:14 }}>
        {/* Prochaines alertes */}
        <div className="card">
          <div className="card-title">
            Prochaines alertes
            <a href="/alertes" style={{ fontSize:12, color:'var(--primary)', fontWeight:500 }}>Voir tout →</a>
          </div>
          {data.prochaines_alertes?.length ? (
            <div>
              {data.prochaines_alertes.map((a, i) => {
                const retard = new Date(a.date_alerte) < new Date()
                return (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom: i < data.prochaines_alertes.length-1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background: retard ? 'var(--danger)' : 'var(--warning)', flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{a.titre}</div>
                      {a.contact_nom && <div style={{ fontSize:11, color:'var(--text3)' }}>{a.contact_nom}</div>}
                    </div>
                    <div style={{ fontSize:12, color: retard ? 'var(--danger)' : 'var(--text3)', fontWeight: retard ? 600 : 400, flexShrink:0 }}>
                      {fmtDate(a.date_alerte)}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={{ padding:'24px 0', textAlign:'center', color:'var(--text3)', fontSize:13 }}>Aucune alerte à venir</div>
          )}
        </div>

        {/* Rapport du jour détail */}
        <div className="card">
          <div className="card-title">
            Rapport du jour
            <a href="/rapports" style={{ fontSize:12, color:'var(--primary)', fontWeight:500 }}>
              {data.rapport_jour ? 'Modifier →' : 'Rédiger →'}
            </a>
          </div>
          {data.rapport_jour ? (
            <div>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10, background: data.rapport_jour.statut==='soumis'?'#e8f5e9':'#fff8e1', color: data.rapport_jour.statut==='soumis'?'#2e7d32':'#e65100' }}>
                  {data.rapport_jour.statut==='soumis'?'✓ Soumis':'✏ Brouillon'}
                </span>
              </div>
              <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.7, marginBottom:8 }}>{data.rapport_jour.contenu}</p>
              {data.rapport_jour.interventions && (
                <p style={{ fontSize:12, color:'var(--text2)' }}><strong>Interventions :</strong> {data.rapport_jour.interventions}</p>
              )}
              {data.rapport_jour.problemes && (
                <p style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}><strong>Problèmes :</strong> {data.rapport_jour.problemes}</p>
              )}
            </div>
          ) : (
            <div style={{ padding:'24px 0', textAlign:'center' }}>
              <i className="ti ti-report" style={{ fontSize:32, color:'var(--text3)', display:'block', marginBottom:8 }} />
              <p style={{ fontSize:13, color:'var(--text3)', marginBottom:12 }}>Vous n'avez pas encore rédigé votre rapport du jour</p>
              <a href="/rapports" className="btn primary" style={{ textDecoration:'none' }}>
                <i className="ti ti-plus" /> Rédiger maintenant
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Derniers rapports équipe visibles */}
      {data.derniers_rapports?.length > 0 && (
        <div className="card">
          <div className="card-title">Rapports récents de l'équipe</div>
          {data.derniers_rapports.map((r, i) => (
            <div key={r.id} style={{ display:'flex', gap:10, padding:'9px 0', borderBottom: i < data.derniers_rapports.length-1?'1px solid var(--border)':'none' }}>
              <div style={{ width:30, height:30, borderRadius:'50%', background:'#f3e5f5', color:'#6a1b9a', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, flexShrink:0 }}>
                {(r.technicien_nom||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{r.technicien_nom} <span style={{ fontWeight:400, color:'var(--text3)' }}>— {fmtDate(r.date_rapport)}</span></div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>{r.contenu?.substring(0,100)}{r.contenu?.length > 100 ? '…' : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}